import { Link } from 'react-router-dom';
import { ChevronRight, Inbox, Loader2 } from 'lucide-react';

export interface HomeCardItem {
  id: string;
  label: string;
  sublabel?: string;
  href?: string;
  badge?: string;
}

export interface HomeCardData {
  id: string;
  title: string;
  items: HomeCardItem[];
}

/**
 * Generische Render-Komponente fuer Heute-Karten (HOME_CARDS aus
 * `/portal/home/today`). Verwendet sowohl in `/uebersicht/heute` (CardBox-
 * Grid) als auch in den persoenlichen Seiten ueber den Widget-Picker
 * (slug `heute.<card-id>`).
 *
 * Wenn `card` nicht gesetzt ist (z.B. Loading), wird ein Skeleton-Header
 * mit Spinner gerendert — passt zum Widget-Grid das eine feste Hoehe
 * erwartet.
 */
export function HomeCard({ card, fallbackTitle }: { card: HomeCardData | null | undefined; fallbackTitle?: string }) {
  if (!card) {
    return (
      <div className="h-full bg-card border border-default rounded-lg overflow-hidden flex flex-col">
        <header className="px-4 py-3 border-b border-default flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">{fallbackTitle ?? '—'}</h2>
        </header>
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-card border border-default rounded-lg overflow-hidden flex flex-col">
      <header className="px-4 py-3 border-b border-default flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">{card.title}</h2>
        <span className="text-xs text-muted-foreground">{card.items.length}</span>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {card.items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Inbox className="w-6 h-6 opacity-40" />
            <span>Keine Eintraege</span>
          </div>
        ) : (
          <ul className="divide-y divide-default">
            {card.items.map((item) => {
              const inner = (
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{item.label}</p>
                    {item.sublabel ? (
                      <p className="text-xs text-muted-foreground truncate">{item.sublabel}</p>
                    ) : null}
                  </div>
                  {item.badge ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary whitespace-nowrap">
                      {item.badge}
                    </span>
                  ) : null}
                  {item.href ? <ChevronRight className="w-4 h-4 text-muted-foreground" /> : null}
                </div>
              );
              return (
                <li key={item.id}>
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="block px-4 py-2.5 hover:bg-background transition-colors"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div className="px-4 py-2.5">{inner}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default HomeCard;
